import { Fragment, type PropsWithChildren } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, Navigate, useLocation, useParams } from "react-router-dom";

import { getPurchaseOrder } from "../../api/purchaseOrders";
import { useActivePurchaseAccess } from "../../hooks/useActivePurchaseAccess";
import { useAttentionRegisterAuthority } from "../../hooks/useAttentionRegisterAuthority";
import { useAuth } from "../../hooks/useAuth";
import { FORM_IDS, PROCESS_IDS } from "../../lib/permissionCatalog";
import { hasPermission } from "../../lib/permissions";
import { useOfflineStatus } from "../../pwa/hooks/useOfflineStatus";
import { RequirePermission } from "./RequirePermission";

interface RequirePurchaseOrderAccessProps extends PropsWithChildren {
  mode: "list" | "detail" | "approval";
}

// D721: this exception belongs to the current PO step, never to generic IAM.
export function RequirePurchaseOrderAccess({ mode, children }: RequirePurchaseOrderAccessProps) {
  const { user } = useAuth();
  const authority = useAttentionRegisterAuthority();
  const { isOnline } = useOfflineStatus();
  const location = useLocation();
  if (mode !== "list") return <PurchaseOrderDetailAccess mode={mode}>{children}</PurchaseOrderDetailAccess>;
  if (user?.type === "office" && new globalThis.URLSearchParams(location.search).get("filter") === "my-approvals") {
    return isOnline ? <Fragment key={authority}>{children}</Fragment>
      : <p role="status">Connect to the internet to load My Approvals.</p>;
  }
  return <RequirePermission forms={[FORM_IDS.PURCHASE_ORDERS]} attentionRegister>{children}</RequirePermission>;
}

function PurchaseOrderDetailAccess({ mode, children }: RequirePurchaseOrderAccessProps) {
  const { user } = useAuth();
  const { user: activeUser, isLoading } = useActivePurchaseAccess();
  const authority = useAttentionRegisterAuthority(activeUser);
  const { isOnline } = useOfflineStatus();
  const { id } = useParams();
  const options = {
    forms: [FORM_IDS.PURCHASE_ORDERS],
    ...(mode === "approval" ? { processes: [PROCESS_IDS.PO_APPROVE] } : {}),
  };
  const ownPermission = hasPermission(activeUser ?? user, options);
  const office = user?.type === "office";
  const probeRequired = office && !ownPermission && !isLoading;
  const probe = useQuery({
    queryKey: ["purchase-order-delegation-access", id, authority],
    queryFn: () => getPurchaseOrder(id ?? ""),
    enabled: probeRequired && Boolean(id) && isOnline,
    retry: false,
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: "always",
    refetchOnWindowFocus: "always",
  });

  if (!office || ownPermission) {
    return <RequirePermission {...options}>
      {children}
    </RequirePermission>;
  }
  if (!isOnline) return <p role="status">Connect to the internet to check current approval access.</p>;
  if (isLoading || probe.isPending || probe.isFetching) return <p role="status">Checking approval access...</p>;
  if (probe.isError) {
    const status = (probe.error as { response?: { status?: number } })?.response?.status;
    if (status === 403 || status === 404) return <Navigate replace to="/dashboard" />;
    return <div role="alert" className="space-y-3 rounded border border-red-200 bg-red-50 p-4 text-sm text-red-700">
      <p>Unable to check current approval access.</p>
      <button type="button" className="rounded border border-red-300 px-3 py-2" onClick={() => { void probe.refetch(); }}>Retry</button>
      <Link className="ml-3 underline" to="/purchase-orders?filter=my-approvals">My Approvals</Link>
    </div>;
  }
  const access = probe.data?.data.approval_access;
  if (probe.data?.data.id !== id || !access?.can_take_action || !access.acts_via_delegation) {
    return <Navigate replace to="/dashboard" />;
  }
  return <Fragment key={authority}>{children}</Fragment>;
}
